import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FrontendGuest } from '@/services/transform';

const EXPERTISE_ZH = ['人工智能', '量子计算', '材料科学', '生物信息学', '清洁能源', '机器人', '区块链', '神经科学', '气候科学', '纳米技术', '合成生物学', '数字经济'];
const EXPERTISE_EN = ['Artificial Intelligence', 'Quantum Computing', 'Materials Science', 'Bioinformatics', 'Clean Energy', 'Robotics', 'Blockchain', 'Neuroscience', 'Climate Science', 'Nanotechnology', 'Synthetic Biology', 'Digital Economy'];

const FIELD_ICONS: Record<string, string> = {
  '人工智能': 'ri-brain-line',
  'Artificial Intelligence': 'ri-brain-line',
  '量子计算': 'ri-cpu-line',
  'Quantum Computing': 'ri-cpu-line',
  '材料科学': 'ri-flask-line',
  'Materials Science': 'ri-flask-line',
  '生物信息学': 'ri-dna-line',
  'Bioinformatics': 'ri-dna-line',
  '清洁能源': 'ri-sun-line',
  'Clean Energy': 'ri-sun-line',
  '机器人': 'ri-robot-line',
  'Robotics': 'ri-robot-line',
  '区块链': 'ri-links-line',
  'Blockchain': 'ri-links-line',
  '神经科学': 'ri-mental-health-line',
  'Neuroscience': 'ri-mental-health-line',
  '气候科学': 'ri-earth-line',
  'Climate Science': 'ri-earth-line',
  '纳米技术': 'ri-microscope-line',
  'Nanotechnology': 'ri-microscope-line',
  '合成生物学': 'ri-leaf-line',
  'Synthetic Biology': 'ri-leaf-line',
  '数字经济': 'ri-bar-chart-line',
  'Digital Economy': 'ri-bar-chart-line',
};

const FIELD_COLORS = [
  { bg: 'bg-[#0A2540]/8', activeCls: 'bg-[#0A2540] text-white border-[#0A2540]', passiveCls: 'text-[#0A2540]' },
  { bg: 'bg-[#2C7DA0]/10', activeCls: 'bg-[#2C7DA0] text-white border-[#2C7DA0]', passiveCls: 'text-[#2C7DA0]' },
  { bg: 'bg-emerald-50', activeCls: 'bg-emerald-600 text-white border-emerald-600', passiveCls: 'text-emerald-700' },
  { bg: 'bg-amber-50', activeCls: 'bg-amber-500 text-white border-amber-500', passiveCls: 'text-amber-700' },
  { bg: 'bg-rose-50', activeCls: 'bg-rose-500 text-white border-rose-500', passiveCls: 'text-rose-600' },
  { bg: 'bg-violet-50', activeCls: 'bg-violet-600 text-white border-violet-600', passiveCls: 'text-violet-700' },
  { bg: 'bg-teal-50', activeCls: 'bg-teal-600 text-white border-teal-600', passiveCls: 'text-teal-700' },
  { bg: 'bg-orange-50', activeCls: 'bg-orange-500 text-white border-orange-500', passiveCls: 'text-orange-600' },
  { bg: 'bg-cyan-50', activeCls: 'bg-cyan-600 text-white border-cyan-600', passiveCls: 'text-cyan-700' },
  { bg: 'bg-lime-50', activeCls: 'bg-lime-600 text-white border-lime-600', passiveCls: 'text-lime-700' },
  { bg: 'bg-pink-50', activeCls: 'bg-pink-500 text-white border-pink-500', passiveCls: 'text-pink-600' },
  { bg: 'bg-indigo-50', activeCls: 'bg-indigo-600 text-white border-indigo-600', passiveCls: 'text-indigo-700' },
];

interface Props {
  guests: FrontendGuest[];
  selectedExpertise: string;
  onSelect: (tag: string) => void;
}

export default function ResearchFieldCloud({ guests, selectedExpertise, onSelect }: Props) {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const fields = isZh ? EXPERTISE_ZH : EXPERTISE_EN;

  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    fields.forEach((field, idx) => {
      const zhTag = EXPERTISE_ZH[idx];
      const enTag = EXPERTISE_EN[idx];
      map[field] = guests.filter(
        (g) =>
          g.expertise.some((e) => e.includes(zhTag)) ||
          g.expertiseEn.some((e) => e.toLowerCase().includes(enTag.toLowerCase()))
      ).length;
    });
    return map;
  }, [fields]);

  const maxCount = Math.max(...Object.values(counts), 1);

  const getFontSize = (count: number) => {
    const ratio = count / maxCount;
    if (ratio >= 0.8) return 'text-base font-bold';
    if (ratio >= 0.6) return 'text-sm font-semibold';
    if (ratio >= 0.4) return 'text-sm font-medium';
    return 'text-xs font-medium';
  };

  return (
    <div className="bg-white rounded-2xl p-5 mb-6 border border-[#0A2540]/8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-[#2C7DA0]/10 rounded-lg text-[#2C7DA0]">
            <i className="ri-price-tag-3-line text-sm" />
          </div>
          <span className="text-[#0A2540] text-sm font-bold">
            {isZh ? '按研究领域筛选' : 'Filter by Research Field'}
          </span>
          <span className="text-[#0A2540]/35 text-xs">
            {isZh ? '— 点击领域标签快速定位专家' : '— Click a field tag to find experts'}
          </span>
        </div>
        {selectedExpertise !== 'all' && (
          <button
            onClick={() => onSelect('all')}
            className="flex items-center gap-1 text-[#2C7DA0] text-xs font-medium hover:text-[#0A2540] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-close-circle-line" />
            {isZh ? '清除' : 'Clear'}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {/* All */}
        <button
          onClick={() => onSelect('all')}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full border transition-all duration-200 cursor-pointer whitespace-nowrap text-sm font-semibold ${
            selectedExpertise === 'all'
              ? 'bg-[#0A2540] text-white border-[#0A2540]'
              : 'bg-[#F7F9FC] text-[#0A2540]/55 border-[#0A2540]/10 hover:border-[#0A2540]/30 hover:text-[#0A2540]'
          }`}
        >
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-apps-line" />
          </div>
          {isZh ? '全部' : 'All'}
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
            selectedExpertise === 'all' ? 'bg-white/20 text-white' : 'bg-[#0A2540]/8 text-[#0A2540]/50'
          }`}>
            {guests.length}
          </span>
        </button>

        {/* Field Tags */}
        {fields.map((field, idx) => {
          const color = FIELD_COLORS[idx % FIELD_COLORS.length];
          const icon = FIELD_ICONS[field] ?? 'ri-focus-3-line';
          const count = counts[field] ?? 0;
          const isActive = selectedExpertise === field;
          const sizeClass = getFontSize(count);

          return (
            <button
              key={field}
              onClick={() => onSelect(field)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all duration-200 cursor-pointer whitespace-nowrap ${sizeClass} ${
                isActive
                  ? `${color.activeCls}`
                  : `${color.bg} ${color.passiveCls} border-transparent hover:brightness-95`
              }`}
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                <i className={icon} />
              </div>
              <span>{field}</span>
              <span className={`text-[10px] font-bold px-1 rounded-full ${
                isActive ? 'bg-white/25 text-white' : 'bg-black/8'
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {selectedExpertise !== 'all' && (
        <div className="mt-3 pt-3 border-t border-[#0A2540]/6 flex items-center gap-2 text-[#0A2540]/45 text-xs">
          <i className="ri-filter-3-line" />
          <span>
            {isZh
              ? `当前显示「${selectedExpertise}」领域的 ${counts[selectedExpertise] ?? 0} 位专家`
              : `Showing ${counts[selectedExpertise] ?? 0} expert(s) in "${selectedExpertise}"`}
          </span>
        </div>
      )}
    </div>
  );
}
